<template>
  <div
    class="flow-component absolute left-4 z-50 block cursor-default rounded bg-white shadow ring-1 ring-surface-900/10 dark:bg-slate-900 dark:text-white/90 dark:ring-surface-200/20"
    :class="[
      route.meta?.fullScreen ? 'top-4' : 'top-4',
      isCollapsed ? 'w-[18rem]' : 'w-[18rem] space-y-4 pt-4'
    ]"
  >
    <div
      class="flex items-center justify-between space-x-1 px-4"
      :class="isCollapsed ? 'py-3' : 'mb-4'"
    >
      <div class="flex items-center space-x-1">
        <div
          class="text-primary-600 border-primary-600/50 border-b-2 opacity-90 hover:opacity-100 dark:text-sky-400"
        >
          <router-link
            :to="{
              name: ROUTES.OFFERFUNNELS
            }"
          >
            Flows
          </router-link>
        </div>
        <ChevronRightIcon class="w-4" />
        <div
          v-if="offerFunnel"
          class="max-w-[10rem] truncate pr-2 font-bold"
          :title="offerFunnel?.name"
        >
          {{ offerFunnel?.name }}
        </div>
      </div>

      <!-- Toggle button -->
      <button
        class="ml-auto rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        :title="isCollapsed ? 'Expand' : 'Collapse'"
        @click="isCollapsed = !isCollapsed"
      >
        <component
          :is="isCollapsed ? ChevronDownIcon : ChevronUpIcon"
          class="h-5 w-5"
        />
      </button>
    </div>

    <!-- Main content - hidden when collapsed -->
    <template v-if="!isCollapsed">
      <Tabs
        v-model:value="activeTab"
        class="px-4 pb-3"
      >
        <TabList class="mb-3 text-[0.8125rem]">
          <Tab value="add">
            Build
          </Tab>
          <Tab
            v-if="globalSettings?.enhancedFlowReporting"
            value="stats"
          >
            Metrics
          </Tab>
          <Tab
            v-if="globalSettings?.enhancedFlowReporting"
            value="logs"
          >
            Logs
          </Tab>
          <Tab value="settings">
            Settings
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel value="add">
            <div class="select-none space-y-4">
              <div v-for="group in availableNodes">
                <div class="text-primary-600 dark:text-white/50 text-xs font-bold uppercase">
                  {{ group.label }}
                </div>

                <div class="mt-1.5 grid grid-cols-2 gap-1.5">
                  <div
                    v-for="(node, nodeIndex) in group.options.filter(
                      (node) => !node.comingsoon
                    )"
                    :key="`${group.label}-${nodeIndex}`"
                    :class="[
                      'relative flex items-center gap-x-1.5 rounded border px-2 py-1.5 text-sm transition-all',
                      node.locked
                        ? 'cursor-not-allowed border-gray-200/50 bg-gray-50/30 opacity-60 dark:border-gray-700/30 dark:bg-gray-800/20'
                        : 'cursor-grab border-gray-200/80 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-100/80 hover:shadow-sm active:cursor-grabbing dark:border-gray-700/50 dark:bg-gray-800/30 dark:hover:border-gray-600 dark:hover:bg-gray-700/50'
                    ]"
                    :draggable="!node.locked"
                    :title="node.locked ? node.lockedMessage : ''"
                    @dragstart="!node.locked && handleDragStart($event, node)"
                    @dragend="!node.locked && handleDragEnd($event, node)"
                    @mouseenter="(e) => handleShowTooltip(e, node)"
                    @mouseleave="hideTooltip"
                  >
                    <iconify-icon
                      v-if="node.icon"
                      :icon="node.locked ? 'hugeicons:lock' : node.icon"
                      height="14"
                      width="14"
                      class="flex-shrink-0 text-gray-500 dark:text-gray-400"
                    />
                    <span class="text-gray-700 dark:text-gray-200">{{ node.label }}</span>
                    <Tag
                      v-if="node.locked"
                      value="Pro"
                      severity="warn"
                      class="ml-auto"
                    />
                    <Tag
                      v-else-if="node.comingsoon"
                      value="Soon"
                      severity="info"
                    />
                    <img
                      v-if="node.api"
                      :src="node.api"
                      class="h-3 w-3"
                    >
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel
            v-if="globalSettings?.enhancedFlowReporting"
            value="stats"
          >
            <div class="space-y-6">
              <!-- Date Range Picker -->
              <div class="flex items-center gap-2">
                <AppDateRangePicker
                  v-model="dateRange"
                  direction="right"
                />
                <span class="text-sm text-gray-600 dark:text-gray-400">
                  {{ formattedDateRange }}
                </span>
              </div>

              <!-- Analytics Toggles -->
              <div class="space-y-3">
                <div class="flex items-center gap-3">
                  <ToggleSwitch v-model="showHeatMap" />
                  <span class="text-sm text-gray-700 dark:text-white">Enable Heat Map</span>
                </div>
                <div class="flex items-center gap-3">
                  <ToggleSwitch v-model="showPathFlow" />
                  <span class="text-sm text-gray-700 dark:text-white">Show Path Flow</span>
                </div>
              </div>

              <!-- Loading State -->
              <div
                v-if="isLoadingMetrics"
                class="space-y-6"
              >
                <div class="space-y-3">
                  <Skeleton
                    width="40%"
                    height="0.875rem"
                    class="!bg-gray-200 dark:!bg-gray-700"
                  />
                  <div class="space-y-2">
                    <div
                      v-for="i in 5"
                      :key="i"
                      class="flex items-center justify-between"
                    >
                      <Skeleton
                        :width="`${40 + (i * 5)}%`"
                        height="0.75rem"
                        class="!bg-gray-200 dark:!bg-gray-700"
                      />
                      <Skeleton
                        width="3rem"
                        height="0.75rem"
                        class="!bg-gray-200 dark:!bg-gray-700"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Stats Content -->
              <div v-else-if="flowMetrics">
                <!-- Overall Flow Stats -->
                <div class="space-y-3">
                  <h3
                    class="text-sm font-semibold text-gray-700 dark:text-white"
                  >
                    Flow Performance
                  </h3>
                  <div class="space-y-2 text-sm">
                    <div class="flex justify-between">
                      <span class="text-gray-600 dark:text-gray-400">
                        Total Executions
                      </span>
                      <span class="font-medium">
                        {{
                          flowMetrics.flowMetrics.totalExecutions.toLocaleString()
                        }}
                      </span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-gray-600 dark:text-gray-400">
                        Unique Visitors
                      </span>
                      <span class="font-medium">
                        {{
                          flowMetrics.flowMetrics.uniqueVisitors.toLocaleString()
                        }}
                      </span>
                    </div>
                    <div class="flex justify-between">
                      <span
                        class="text-gray-600 dark:text-gray-400"
                        :class="identifiedMetricClass"
                      >
                        Identified Visitors
                      </span>
                      <span
                        class="font-medium"
                        :class="identifiedMetricClass"
                      >
                        {{ flowMetrics.flowMetrics.identifiedVisitors.toLocaleString() }}
                        <span
                          v-if="flowMetrics.flowMetrics.uniqueVisitors > 0"
                        >({{ identifiedVisitorRate.toFixed(1) }}%)</span>
                      </span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-gray-600 dark:text-gray-400">
                        {{ isSystemFlow ? 'Avg. Time in Flow' : 'Avg. Time to Complete' }}
                      </span>
                      <span class="font-medium">
                        {{
                          formatDuration(flowMetrics.flowMetrics.avgDurationMs)
                        }}
                      </span>
                    </div>
                    <div
                      v-if="!isSystemFlow"
                      class="flex justify-between"
                    >
                      <span class="text-gray-600 dark:text-gray-400">
                        Completion Rate
                      </span>
                      <span class="font-medium">
                        {{
                          calculateCompletionRate(
                            flowMetrics.flowMetrics.completionBreakdown
                          )
                        }}%
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  v-if="identificationWarning"
                  class="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200"
                >
                  <div class="font-medium">
                    This survey may not be identifying visitors.
                  </div>
                  <p class="mt-1">
                    It has {{ identificationWarning.syncedQuestionCount }} synced
                    {{ identificationWarning.syncedQuestionCount === 1 ? 'question' : 'questions' }},
                    but only {{ identificationWarning.identifiedRate }}% of recent
                    executions were identified across
                    {{ identificationWarning.totalExecutions }} executions. If this
                    flow runs after an opt-in or external form redirect, make sure
                    the thank-you page URL preserves the identifying parameters so
                    people can be segmented in
                    {{ integratedPartner?.label || 'your ESP' }}.
                  </p>
                </div>

                <!-- Completion Breakdown -->
                <div
                  v-if="flowMetrics.flowMetrics.completionBreakdown"
                  class="mt-6 space-y-3"
                >
                  <h3
                    class="text-sm font-semibold text-gray-700 dark:text-white"
                  >
                    Exit Reasons
                  </h3>
                  <div class="space-y-2 text-sm">
                    <div
                      v-for="(count, status) in flowMetrics.flowMetrics
                        .completionBreakdown"
                      :key="status"
                      class="flex justify-between"
                    >
                      <span class="capitalize text-gray-600 dark:text-gray-400">
                        {{ status }}
                      </span>
                      <span class="font-medium">{{ count }}</span>
                    </div>
                  </div>
                </div>

                <!-- Key Insights -->
                <div
                  v-if="keyInsights"
                  class="mt-6 space-y-3"
                >
                  <h3
                    class="text-sm font-semibold text-gray-700 dark:text-white"
                  >
                    Key Insights
                  </h3>
                  <div class="space-y-3">
                    <!-- Highest Drop-off Question -->
                    <div
                      v-if="keyInsights.highestDropOffQuestion"
                      class="rounded border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800"
                    >
                      <div class="mb-1 flex items-center justify-between">
                        <span
                          class="text-xs font-medium text-gray-600 dark:text-gray-400"
                        >
                          Highest Drop-off Question
                        </span>
                        <iconify-icon
                          icon="hugeicons:target-01"
                          width="16"
                          class="cursor-pointer text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          @click="
                            handleFocusNode(keyInsights.highestDropOffQuestion)
                          "
                        />
                      </div>
                      <div class="text-sm font-medium text-gray-900 dark:text-white">
                        {{ keyInsights.highestDropOffQuestion.label }}
                      </div>
                      <div
                        class="mt-1 text-xs text-gray-500 dark:text-gray-300"
                      >
                        {{
                          keyInsights.highestDropOffQuestion.dropOffRate.toFixed(
                            1
                          )
                        }}% drop-off rate ({{
                          keyInsights.highestDropOffQuestion.dropOffCount
                        }}
                        of
                        {{ keyInsights.highestDropOffQuestion.visitorCount }}
                        visitors)
                      </div>
                    </div>

                    <!-- Highest Abandonment Offer -->
                    <div
                      v-if="keyInsights.highestAbandonmentOffer"
                      class="rounded border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800"
                    >
                      <div class="mb-1 flex items-center justify-between">
                        <span
                          class="text-xs font-medium text-gray-600 dark:text-gray-400"
                        >
                          Highest Abandonment Offer/Form
                        </span>
                        <iconify-icon
                          icon="hugeicons:target-01"
                          width="16"
                          class="cursor-pointer text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          @click="
                            handleFocusNode(keyInsights.highestAbandonmentOffer)
                          "
                        />
                      </div>
                      <div class="text-sm font-medium text-gray-900 dark:text-white">
                        {{ keyInsights.highestAbandonmentOffer.label }}
                      </div>
                      <div
                        class="mt-1 text-xs text-gray-500 dark:text-gray-300"
                      >
                        {{
                          keyInsights.highestAbandonmentOffer.abandonmentRate.toFixed(
                            1
                          )
                        }}% abandonment rate ({{
                          keyInsights.highestAbandonmentOffer.conversions
                        }}
                        conversions from
                        {{ keyInsights.highestAbandonmentOffer.visitorCount }}
                        visitors)
                      </div>
                    </div>

                    <!-- Slowest Question -->
                    <div
                      v-if="keyInsights.slowestQuestion"
                      class="rounded border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800"
                    >
                      <div class="mb-1 flex items-center justify-between">
                        <span
                          class="text-xs font-medium text-gray-600 dark:text-gray-400"
                        >
                          Slowest Question to Answer
                        </span>
                        <iconify-icon
                          icon="hugeicons:target-01"
                          width="16"
                          class="cursor-pointer text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          @click="
                            handleFocusNode(keyInsights.slowestQuestion)
                          "
                        />
                      </div>
                      <div class="text-sm font-medium text-gray-900 dark:text-white">
                        {{ keyInsights.slowestQuestion.label }}
                      </div>
                      <div
                        class="mt-1 text-xs text-gray-500 dark:text-gray-300"
                      >
                        Takes
                        {{
                          formatDuration(
                            keyInsights.slowestQuestion.avgDuration
                          )
                        }}
                        on average
                      </div>
                    </div>

                    <!-- No insights available -->
                    <div
                      v-if="
                        !keyInsights.highestDropOffQuestion &&
                          !keyInsights.highestAbandonmentOffer &&
                          !keyInsights.slowestQuestion
                      "
                      class="py-4 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      Not enough data to generate insights yet
                    </div>
                  </div>
                </div>

                <!-- Drop-off Analysis -->
                <div
                  v-if="dropOffNodes.length > 0"
                  class="space-y-3"
                >
                  <h3
                    class="text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Top Drop-off Points
                  </h3>
                  <div class="space-y-2 text-sm">
                    <div
                      v-for="dropOff in dropOffNodes.slice(0, 5)"
                      :key="dropOff.nodeId"
                      class="flex items-center justify-between"
                    >
                      <span
                        class="flex-1 truncate text-gray-600 dark:text-gray-400"
                      >
                        {{ getNodeLabel(getNodeById(dropOff.nodeId)) }}
                      </span>
                      <span class="ml-2 font-medium">{{ dropOff.count }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Data State -->
              <div
                v-else
                class="py-8 text-center text-gray-500 dark:text-gray-400"
              >
                <iconify-icon
                  icon="hugeicons:analytics-01"
                  width="48"
                  class="mb-2 opacity-50"
                />
                <p class="text-sm">
                  No analytics data available
                </p>
                <p class="mt-1 text-xs">
                  Run this flow to start collecting data
                </p>
              </div>
            </div>
          </TabPanel>
          <TabPanel value="settings">
            <div class="space-y-4">
              <FormGroup label="Name">
                <InputText
                  v-model="localFlowName"
                  class="w-full"
                  size="large"
                />
              </FormGroup>
              <FormGroup
                label="System Flow"
              >
                <div
                  v-tooltip="{
                    value: hasDisplayNodesWarning ? tooltipMessage : null,
                    showDelay: 300,
                    hideDelay: 100
                  }"
                  class="flex items-start gap-3"
                >
                  <ToggleSwitch
                    v-model="isSystemFlowEnabled"
                    :disabled="hasDisplayNodesWarning"
                  />
                  <div class="flex-1">
                    <p class="text-xs text-gray-600 dark:text-gray-400">
                      Want to score or segment people based on what they're doing on your website? Enable this to eliminate the need to associate with widgets or show questions/offers/forms.
                    </p>
                  </div>
                </div>
              </FormGroup>
              <FormGroup
                v-if="!globalSettings?.enhancedFlowReporting"
                label="Metrics Date Range"
              >
                <AppDateRangePicker
                  v-model="dateRange"
                  direction="right"
                />
              </FormGroup>

              <hr class="border-gray-200 dark:border-gray-700">

              <div class="grid grid-cols-2 gap-3">
                <Button
                  label="Duplicate"
                  size="small"
                  @click="promptDuplicate"
                />
                <Button
                  label="Delete"
                  severity="danger"
                  size="small"
                  @click="promptDelete"
                />
              </div>
              <div
                class="text-brand-link hover:text-brand-linkHover flex justify-between gap-x-2 text-xs underline"
              >
                <div class="flex gap-x-2">
                  <div
                    class="cursor-pointer"
                    @click="copyFlowForLLMs"
                  >
                    Copy LLM Output
                  </div>
                  <div
                    v-if="isLocal"
                    class="cursor-pointer"
                    @click="copyFlowJson"
                  >
                    Copy JSON
                  </div>
                </div>
                <div
                  v-if="globalSettings?.enhancedFlowReporting"
                  class="cursor-pointer"
                  @click="promptClearAnalytics"
                >
                  Delete Analytics Data
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel
            v-if="globalSettings?.enhancedFlowReporting"
            value="logs"
          >
            <!-- Filter & Engagement Drawer -->
            <div class="mb-3 flex items-center justify-between">
              <label class="flex cursor-pointer items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <input
                  v-model="showIdentifiedOnly"
                  type="checkbox"
                  class="h-3.5 w-3.5 rounded border-gray-300 text-sky-600 focus:ring-sky-500 dark:border-gray-600 dark:bg-gray-700"
                >
                Show only identified visitors
              </label>
              <Button
                label="Detailed View"
                icon="pi pi-external-link"
                severity="secondary"
                text
                size="small"
                class="!text-xs !py-0.5"
                @click="showEngagementDrawer = true"
              />
            </div>
            <div
              class="max-h-[60vh] space-y-4 overflow-y-auto"
              @wheel.stop
            >
              <!-- Loading State -->
              <div
                v-if="isLoadingRecentVisitors"
                class="space-y-2"
              >
                <div
                  v-for="i in 5"
                  :key="i"
                  class="flex items-center gap-3 rounded border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800"
                >
                  <Skeleton
                    shape="circle"
                    size="2rem"
                    class="!bg-gray-200 dark:!bg-gray-700"
                  />
                  <div class="flex-1 space-y-1.5">
                    <Skeleton
                      width="60%"
                      height="0.875rem"
                      class="!bg-gray-200 dark:!bg-gray-700"
                    />
                    <Skeleton
                      width="40%"
                      height="0.75rem"
                      class="!bg-gray-200 dark:!bg-gray-700"
                    />
                  </div>
                </div>
              </div>

              <!-- Visitor List -->
              <div
                v-else-if="recentFlowVisitors && recentFlowVisitors.length > 0"
                class="space-y-2"
              >
                <!-- Show message when displaying fewer than total -->
                <div
                  v-if="showingFewerThanTotal"
                  class="text-xs text-gray-500 dark:text-gray-400"
                >
                  Showing most recent {{ recentFlowVisitors.length }} of {{ totalFlowVisitors }} visitors
                </div>
                <div
                  v-for="visitor in recentFlowVisitors"
                  :key="visitor.visitorId"
                  class="flex cursor-pointer items-center gap-3 rounded border border-gray-200 bg-gray-50 p-2 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                  @click="navigateToVisitor(visitor)"
                  @mouseenter="handleVisitorHover($event, visitor)"
                  @mouseleave="hideTooltip"
                >
                  <!-- Gravatar -->
                  <img
                    v-if="visitor.emailAddress"
                    :src="getGravatarUrl(visitor.emailAddress)"
                    class="h-8 w-8 rounded-full"
                    alt=""
                  >
                  <div
                    v-else
                    class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-300 text-xs font-medium text-gray-600 dark:bg-gray-600 dark:text-gray-300"
                  >
                    AN
                  </div>

                  <!-- Visitor Info -->
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-sm font-medium text-gray-900 dark:text-white">
                      {{ visitor.emailAddress || 'Anonymous Visitor' }}
                    </div>
                    <div class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span :class="getStatusClass(visitor.completionStatus)">
                        {{ formatCompletionStatus(visitor.completionStatus) }}
                      </span>
                      <span>&middot;</span>
                      <span>{{ formatTimeAgo(visitor.timestamp) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Visitors State -->
              <div
                v-else
                class="py-8 text-center text-gray-500 dark:text-gray-400"
              >
                <iconify-icon
                  icon="hugeicons:user-group"
                  width="48"
                  class="mb-2 opacity-50"
                />
                <p class="text-sm">
                  No visitors yet
                </p>
                <p class="mt-1 text-xs">
                  Visitors will appear here after running this flow
                </p>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </template>

    <!-- Rich Tooltip -->
    <RichTooltip
      :content="currentTooltip"
      :position="tooltipPosition"
    />

    <FlowEngagementDrawer
      v-model:visible="showEngagementDrawer"
      :flow-id="(route.params.offerFunnelId as string)"
    />
    <Dialog
      v-model:visible="showDeleteFlowModal"
      modal
      header="Delete Flow?"
      :style="{ width: '42rem' }"
      :closable="false"
    >
      <div class="space-y-4">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          This flow references questions, offers, or forms. Choose what else
          should be deleted with it.
        </p>
        <div
          v-if="associatedWidgetsSummary"
          class="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          {{ associatedWidgetsSummary }}
        </div>
        <div
          class="max-h-60 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3 dark:border-gray-700"
        >
          <div
            v-for="reference in referencedFlowResources"
            :key="reference.key"
            class="border-b border-gray-100 pb-2 last:border-b-0 last:pb-0 dark:border-gray-800"
          >
            <div class="text-sm font-medium text-gray-900 dark:text-white">
              {{ reference.label }}
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400">
              {{ flowReferenceKindLabel(reference) }} ·
              {{ flowReferenceUsage(reference) }}
            </div>
          </div>
        </div>
        <div class="space-y-3">
          <div class="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <div class="space-y-3">
              <div class="space-y-1">
                <div class="text-sm font-medium text-gray-900 dark:text-white">
                  1) Delete flow only
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Keeps attached widgets, questions, offers, and forms. Any
                  attached widgets will be left in place but detached from this
                  flow.
                </p>
              </div>
              <Button
                label="Delete Flow Only"
                severity="danger"
                :disabled="isDeletingFlow"
                @click="deleteFlowOnly"
              />
            </div>
          </div>
          <div class="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <div class="space-y-3">
              <div class="space-y-1">
                <div class="text-sm font-medium text-gray-900 dark:text-white">
                  2) Delete flow + widget(s)
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Deletes this flow and any attached widgets, but keeps the
                  referenced questions, offers, and forms.
                </p>
              </div>
              <Button
                label="Delete Flow + Widgets"
                severity="secondary"
                outlined
                size="small"
                :disabled="isDeletingFlow"
                @click="deleteFlowAndWidgets"
              />
            </div>
          </div>
          <div class="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <div class="space-y-3">
              <div class="space-y-1">
                <div class="text-sm font-medium text-gray-900 dark:text-white">
                  3) Delete flow + widget(s) + all referenced things
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Deletes this flow, any attached widgets, and the referenced
                  questions, offers, and forms — including any campaigns built
                  on this flow's questions. This can break other flows that
                  still reference them.
                </p>
              </div>
              <Button
                label="Delete Widgets + Referenced"
                severity="secondary"
                outlined
                size="small"
                :disabled="isDeletingFlow"
                @click="deleteFlowAndWidgetsAndReferencedThings"
              />
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          outlined
          :disabled="isDeletingFlow"
          @click="showDeleteFlowModal = false"
        />
      </template>
    </Dialog>


    <!-- Errors section - always visible -->
    <div
      v-if="errorsWithNodes.length > 0 || unsyncedQuestions.length > 0"
      class="text-brand-text space-y-4 border-t-2 border-yellow-300 bg-yellow-200 px-4 py-3 dark:border-yellow-800 dark:bg-yellow-950 dark:text-white/90"
    >
      <div class="flex items-center justify-between gap-x-4 text-sm">
        <div class="font-bold">
          This Flow has {{ errorsWithNodes.length > 0 ? 'errors' : 'warnings' }}:
        </div>
        <ExclamationTriangleIcon class="h-6 w-6" />
      </div>
      <div class="pb-3 text-sm">
        <ul class="ml-4 list-outside list-disc">
          <li
            v-for="(error, index) in errorsWithNodes"
            :key="index"
            class="pb-1"
          >
            <template v-if="error.node">
              <span
                class="text-brand-link cursor-pointer hover:underline dark:text-blue-300/80"
                @click="emits('focusNode', error.node)"
              >
                {{ error.message }}
              </span>
            </template>
            <template v-else>
              {{ error.message }}
            </template>
          </li>
          <li
            v-if="unsyncedQuestions.length > 0"
            class="pb-1"
          >
            {{ unsyncedQuestions.length }} {{ unsyncedQuestions.length === 1 ? 'question doesn\'t' : 'questions don\'t' }} sync to {{ integratedPartner?.label || 'your ESP' }}.
            <span
              class="text-brand-link cursor-pointer hover:underline dark:text-blue-300/80"
              @click="showBulkSyncModal = true"
            >
              Fix this.
            </span>
          </li>
        </ul>
      </div>
    </div>

    <div
      v-if="identificationWarning"
      class="space-y-3 border-t-2 border-red-300 bg-red-50 px-4 py-3 text-red-900 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-100"
    >
      <div class="flex items-center justify-between gap-x-4 text-sm">
        <div class="font-bold">
          This survey may not be identifying visitors:
        </div>
        <ExclamationTriangleIcon class="h-6 w-6" />
      </div>
      <p class="text-sm">
        It has {{ identificationWarning.syncedQuestionCount }} synced
        {{ identificationWarning.syncedQuestionCount === 1 ? 'question' : 'questions' }},
        but only {{ identificationWarning.identifiedRate }}% of recent executions
        were identified across {{ identificationWarning.totalExecutions }} executions.
        If this flow runs after an opt-in or external form redirect, make sure the
        thank-you page URL preserves the identifying parameters so people can be
        segmented in {{ integratedPartner?.label || 'your ESP' }}.
      </p>
    </div>

    <!-- Bulk Sync Fields Modal -->
    <BulkSyncFieldsModal
      v-model="showBulkSyncModal"
      :questions="unsyncedQuestions"
      @synced="showBulkSyncModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { ChevronRightIcon } from '@heroicons/vue/20/solid'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/20/solid'
import {
  CubeTransparentIcon as AIGenerateIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import widgetTypeDefinitions from '@/modules/widget/data/widgetTypeDefinitions'
import { flowErrorsWithNodes, questionsInFlow } from '@/modules/flow/services/flowUtilities'
import { segmentGroupSyncSetupCompletion } from '@/modules/segment/services/segmentUtilities'
import {
  scoresInFlow,
  funnelDefinitionEach,
  getNodeByNodeId,
  flowHasDisplayNodes,
  renderText
} from '@rightmessage/flow-builder'
import {
  deleteFlowExecutions,
  fetchFlowSummaryMetrics,
  fetchRecentFlowVisitors,
  type RecentFlowVisitor
} from '@/modules/flow/services/flowMetricsService'
import {
  assessFlowIdentificationHealth,
  dimensionHasSyncConfiguration,
  flowHasExistingIdentitySignal,
  type DimensionSyncStatus
} from '@/modules/flow/services/flowIdentificationHealth'
import BulkSyncFieldsModal from './BulkSyncFieldsModal.vue'
import { getGravatarUrl } from '@/modules/visitors/services/peopleUtilities'
import { ROUTES } from '@/router/routeNames'
import { segmentName } from '@/modules/segment/services/segmentUtilities'
import { useTooltip } from '@/composables/useTooltip'
import RichTooltip from '@/components/common/RichTooltip.vue'
import FlowEngagementDrawer from './FlowEngagementDrawer.vue'
import { useAvailableNodes } from '@/modules/flow/composables/useAvailableNodes'
import type { Widget } from '@/types/config'

const confirm = inject('confirm')

// Define props
const props = defineProps<{
  initialTab?: string
}>()

// Define emits
const emits = defineEmits(['focusNode'])
type FlowReference = {
  key: string
  id: string
  kind: 'question' | 'offer' | 'form' | 'campaign'
  label: string
  dimensionId: string | null
  sharedBy: string[]
}

type DeleteFlowOptions = {
  deleteWidgets: boolean
  deleteReferencedThings: boolean
}


// Tooltip
const { currentTooltip, tooltipPosition, showTooltip, hideTooltip, clearTooltip } = useTooltip()

// Tab and collapse state
const activeTab = ref(props.initialTab || 'add')
const isCollapsed = ref(false)
const showEngagementDrawer = ref(false)
const showDeleteFlowModal = ref(false)
const isDeletingFlow = ref(false)

// Watch for initialTab changes to switch tabs
watch(() => props.initialTab, (newTab) => {
  if (newTab) {
    activeTab.value = newTab
    isCollapsed.value = false
  }
})

const flowStore = useFlowStore()
const flowBuilderStore = useFlowBuilderStore()
const { showHeatMap, showPathFlow } = storeToRefs(flowBuilderStore)
const widgetStore = useWidgetStore()
const teamStore = useTeamStore()
const flowMetricsStore = useFlowMetricsStore()
const settingsStore = useSettingsStore()
const { settings: globalSettings } = storeToRefs(settingsStore)
const segmentGroupStore = useSegmentGroupStore()
const offerStore = useOfferStore()
const questionStore = useQuestionStore()
const campaignStore = useCampaignStore()
const notificationStore = useNotificationStore()
const integrationStore = useIntegrationStore()
const { integratedPartner } = storeToRefs(integrationStore)

// Use the extracted available nodes composable
const { availableNodes } = useAvailableNodes({
  flowId: computed(() => route.params.offerFunnelId as string)
})

const reportStore = useReportStore()
const { dateRange } = storeToRefs(reportStore)

// Format date range for display (e.g., "Jun 1 - Jun 9")
const formattedDateRange = computed(() => {
  if (!dateRange.value?.start || !dateRange.value?.end) return ''

  const start = new Date(dateRange.value.start)
  const end = new Date(dateRange.value.end)

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return `${formatDate(start)} - ${formatDate(end)}`
})

const route = useRoute()
const router = useRouter()

const offerFunnel = computed(() => {
  return flowStore.getById(route.params.offerFunnelId)
})

const isSystemFlow = computed(() => offerFunnel.value?.isSystemFlow === true)

// Debounced flow name to prevent config mutation lag on every keystroke
const localFlowName = useDebouncedField(
  () => offerFunnel.value?.name || '',
  (v) => { if (offerFunnel.value) offerFunnel.value.name = v },
)

const errors = computed(() => flowErrors(offerFunnel.value))
const errorsWithNodes = computed(() => flowErrorsWithNodes(offerFunnel.value))

// Bulk sync fields modal
const showBulkSyncModal = ref(false)

// Check if integration supports custom field creation (or is unstructured)
const canBulkSyncFields = computed(() => {
  if (!integratedPartner.value) return false
  const customField = integratedPartner.value?.espFeatureSupport?.customField
  if (!customField || typeof customField !== 'object') return false
  // Show if integration supports creating fields OR is unstructured
  return customField.create === true || customField.unstructured === true
})


const flowQuestions = computed(() =>
  offerFunnel.value?.funnelDefinition
    ? questionsInFlow(offerFunnel.value.funnelDefinition)
    : []
)

const dimensionSyncStatusById = computed<Record<string, DimensionSyncStatus>>(() => {
  const syncStatuses: Record<string, DimensionSyncStatus> = {}

  for (const question of flowQuestions.value) {
    if (!question.dimensionId) continue

    const dimension = segmentGroupStore.getById(question.dimensionId)
    syncStatuses[question.dimensionId] = dimensionHasSyncConfiguration(dimension)
      ? 'all_values'
      : 'no_values'
  }

  return syncStatuses
})

const hasExistingIdentitySignal = computed(() =>
  flowHasExistingIdentitySignal(offerFunnel.value)
)
// Find questions in the flow that don't sync to a custom field or tag
const unsyncedQuestions = computed(() => {
  if (!canBulkSyncFields.value || !offerFunnel.value?.funnelDefinition) return []

  const result: Array<{
    id: string
    label: string
    dimensionId?: string | null
    dimensionName?: string
    isInput: boolean
  }> = []

  for (const q of flowQuestions.value) {
    if (q.dimensionId) {
      // Dimension-linked question: use existing sync completion checker
      const dimension = segmentGroupStore.getById(q.dimensionId)
      if (!dimension) continue
      const syncStatus = segmentGroupSyncSetupCompletion(dimension)
      // Only flag dimensions with no custom field assigned (truly unsynced)
      if (syncStatus !== 'no_field') continue

      result.push({
        id: q.id,
        label: q.label,
        dimensionId: q.dimensionId,
        dimensionName: dimension.name,
        isInput: false
      })
    } else {
      // Input question: check question-level sync
      const question = questionStore.getById(q.id)
      if (!question) continue
      if (question.syncToField) continue

      result.push({
        id: q.id,
        label: q.label,
        dimensionId: null,
        dimensionName: undefined,
        isInput: true
      })
    }
  }

  return result
})

// System Flow management
const isSystemFlowEnabled = computed({
  get: () => offerFunnel.value?.isSystemFlow || false,
  set: (value) => {
    if (offerFunnel.value) {
      offerFunnel.value.isSystemFlow = value
    }
  }
})

const hasDisplayNodesWarning = computed(() => {
  if (!offerFunnel.value?.funnelDefinition) return false

  // Check if flow has Display nodes
  const hasDisplayNodes = flowHasDisplayNodes(offerFunnel.value.funnelDefinition)

  // Check if flow has attached widgets
  const attachedWidgets = widgetStore.getWidgetsForOfferFunnel(offerFunnel.value.id)
  const hasAttachedWidgets = attachedWidgets.length > 0

  return hasDisplayNodes || hasAttachedWidgets
})

const tooltipMessage = computed(() => {
  if (!offerFunnel.value?.funnelDefinition) return ''

  const hasDisplayNodes = flowHasDisplayNodes(offerFunnel.value.funnelDefinition)
  const attachedWidgets = widgetStore.getWidgetsForOfferFunnel(offerFunnel.value.id)
  const hasAttachedWidgets = attachedWidgets.length > 0

  if (hasDisplayNodes && hasAttachedWidgets) {
    return 'This flow contains Display nodes (Questions, Offers, or Messages) and is attached to widgets. Remove these nodes and detach from all widgets before enabling System Flow mode.'
  } else if (hasDisplayNodes) {
    return 'This flow contains Display nodes (Questions, Offers, or Messages) which require a UI. Remove these nodes before enabling System Flow mode.'
  } else if (hasAttachedWidgets) {
    return 'This flow is attached to widgets. Detach from all widgets before enabling System Flow mode.'
  }
  return ''
})

// Flow metrics for stats tab
const flowMetricsQuery = computed(() => {
  if (!globalSettings.value?.enhancedFlowReporting || !offerFunnel.value) {
    return null
  }
  return flowMetricsStore.flowQueries[offerFunnel.value.id]
})

const isLoadingMetrics = computed(() =>
  Boolean(unref(flowMetricsQuery.value?.isLoading) || unref(flowMetricsQuery.value?.isFetching))
)
const flowMetrics = computed(() => unref(flowMetricsQuery.value?.data) ?? null)

const identifiedVisitorRate = computed(() => {
  const uniqueVisitors = flowMetrics.value?.flowMetrics?.uniqueVisitors ?? 0
  const identifiedVisitors = flowMetrics.value?.flowMetrics?.identifiedVisitors ?? 0

  return uniqueVisitors > 0 ? (identifiedVisitors / uniqueVisitors) * 100 : 0
})

const { data: flowSummaryMetricsData } = useQuery({
  queryKey: computed(() => [
    'flow-summary-warning',
    teamStore.currentTeam?.pid,
    offerFunnel.value?.id,
    dateRange.value?.start,
    dateRange.value?.end
  ]),
  queryFn: async () => {
    if (!teamStore.currentTeam?.pid || !offerFunnel.value?.id) return null

    const response = await fetchFlowSummaryMetrics(teamStore.currentTeam.pid, {
      flowIds: [offerFunnel.value.id],
      start: dateRange.value?.start
        ? new Date(dateRange.value.start)
        : new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      end: dateRange.value?.end ? new Date(dateRange.value.end) : new Date()
    })

    return response.flows?.[offerFunnel.value.id] ?? null
  },
  enabled: computed(() =>
    !!globalSettings.value?.enhancedFlowReporting &&
    !!teamStore.currentTeam?.pid &&
    !!offerFunnel.value?.id
  ),
  staleTime: 30000
})

const flowSummaryMetrics = computed(() => flowSummaryMetricsData.value ?? null)

const summaryIdentifiedRate = computed(() => {
  const uniqueVisitors = flowSummaryMetrics.value?.uniqueVisitors ?? 0
  const identifiedVisitors = flowSummaryMetrics.value?.identifiedVisitors ?? 0

  return uniqueVisitors > 0 ? (identifiedVisitors / uniqueVisitors) * 100 : 0
})

const summaryIdentificationHealth = computed(() =>
  assessFlowIdentificationHealth({
    flow: offerFunnel.value,
    hasExistingIdentitySignal: hasExistingIdentitySignal.value,
    questions: flowQuestions.value,
    dimensionSyncStatusById: dimensionSyncStatusById.value,
    totalExecutions: flowSummaryMetrics.value?.executions ?? 0,
    identifiedRate: summaryIdentifiedRate.value
  })
)

const identifiedMetricClass = computed(() =>
  summaryIdentificationHealth.value.hasSuspiciouslyLowIdentification
    ? 'text-red-600 dark:text-red-400'
    : ''
)

const identificationWarning = computed(() => {
  if (!summaryIdentificationHealth.value.hasSuspiciouslyLowIdentification || !flowSummaryMetrics.value) {
    return null
  }

  return {
    syncedQuestionCount: summaryIdentificationHealth.value.syncedQuestionCount,
    identifiedRate: summaryIdentifiedRate.value.toFixed(1),
    totalExecutions: flowSummaryMetrics.value.executions.toLocaleString()
  }
})
// Recent flow visitors for Logs tab
const showIdentifiedOnly = ref(false)

const {
  data: recentFlowVisitorsData,
  isLoading: isLoadingRecentVisitors
} = useQuery({
  queryKey: computed(() => [
    'recent-flow-visitors',
    teamStore.currentTeam?.pid,
    offerFunnel.value?.id,
    dateRange.value?.start,
    dateRange.value?.end,
    showIdentifiedOnly.value
  ]),
  queryFn: async () => {
    if (!teamStore.currentTeam?.pid || !offerFunnel.value?.id) return null
    return fetchRecentFlowVisitors(teamStore.currentTeam.pid, {
      flowId: offerFunnel.value.id,
      start: dateRange.value?.start ? new Date(dateRange.value.start) : new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      end: dateRange.value?.end ? new Date(dateRange.value.end) : new Date(),
      limit: 50,
      identifiedOnly: showIdentifiedOnly.value
    })
  },
  enabled: computed(() =>
    !!globalSettings.value?.enhancedFlowReporting &&
    !!teamStore.currentTeam?.pid &&
    !!offerFunnel.value?.id
  ),
  staleTime: 30000 // 30 seconds
})

const recentFlowVisitors = computed(() => recentFlowVisitorsData.value?.visitors ?? [])
const totalFlowVisitors = computed(() => recentFlowVisitorsData.value?.total ?? 0)
const showingFewerThanTotal = computed(() =>
  recentFlowVisitors.value.length > 0 &&
  recentFlowVisitors.value.length < totalFlowVisitors.value
)

// Format completion status for display
const formatCompletionStatus = (status: string) => {
  const statusLabels: Record<string, string> = {
    completed: 'Completed',
    exited: 'Exited',
    dismissed: 'Dismissed',
    abandoned: 'Abandoned',
    inactivity: 'Incomplete',
    forced: 'Redirected'
  }
  return statusLabels[status] || status
}

// Get CSS class for status
const getStatusClass = (status: string) => {
  const statusClasses: Record<string, string> = {
    completed: 'text-green-600 dark:text-green-400',
    exited: 'text-blue-600 dark:text-blue-400',
    dismissed: 'text-yellow-600 dark:text-yellow-400',
    abandoned: 'text-red-600 dark:text-red-400',
    inactivity: 'text-gray-600 dark:text-gray-400',
    forced: 'text-green-600 dark:text-green-400'
  }
  return statusClasses[status] || 'text-gray-600 dark:text-gray-400'
}

// Format time ago
const formatTimeAgo = (timestamp: string) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Navigate to visitor detail
const navigateToVisitor = (visitor: RecentFlowVisitor) => {
  const identifier = visitor.emailAddress
    ? encodeURIComponent(visitor.emailAddress)
    : visitor.visitorId

  router.push({
    name: ROUTES.VISITOR_DETAIL,
    params: { visitorId: identifier }
  })
}

// Build outcome description from flow execution last node
const getFlowOutcomeDescription = (visitor: RecentFlowVisitor): string => {
  const node = visitor.lastNode
  if (!node) return ''

  if (node.nodeType === 'offer_conversion' && node.offerId) {
    const offer = offerStore.getById(node.offerId)
    const offerName = offer?.name || 'an offer'
    const action = node.conversionType === 'submit' ? 'Submitted form' : 'Clicked button'
    return `${action} on "${offerName}"`
  }

  if (node.nodeType === 'redirect_conversion' && node.url) {
    return `Redirected to ${node.url}`
  }

  if (node.nodeType === 'redirect' && node.url) {
    return `Redirected to ${node.url}`
  }

  if (node.nodeType === 'offer' && node.offerId) {
    const offer = offerStore.getById(node.offerId)
    const offerName = offer?.name || 'an offer'
    return `Shown "${offerName}"`
  }

  if (node.nodeType === 'exit') {
    return 'Exited flow'
  }

  if (node.nodeType === 'message') {
    return 'Shown message'
  }

  // For sg (segment group) nodes with ask action - they were shown a question
  if (node.nodeType === 'sg' && node.action === 'ask' && node.segmentGroupId) {
    const question = questionStore.getBySegmentGroupId(node.segmentGroupId)
    if (question?.text) {
      // Use renderText to handle Liquid templates and HTML
      const cleanText = renderText(question.text, {}, 100) || question.text.replace(/<[^>]*>/g, '').trim()
      return `Shown question: "${cleanText}"`
    }
    // Fall back to dimension name
    const dimension = segmentGroupStore.getById(node.segmentGroupId)
    if (dimension?.name) {
      return `Shown question about "${dimension.name}"`
    }
    return 'Shown a question'
  }

  return ''
}

// Truncate URL for display
const truncateUrl = (url: string, maxLength: number = 50): string => {
  if (!url || url.length <= maxLength) return url
  // Try to truncate at a path boundary
  const ellipsis = '...'
  const availableLength = maxLength - ellipsis.length
  return url.substring(0, availableLength) + ellipsis
}

// Handle visitor hover to show tooltip
const handleVisitorHover = (event: MouseEvent, visitor: RecentFlowVisitor) => {
  const pageUrl = visitor.pageUrl || 'Unknown page'
  const truncatedUrl = truncateUrl(pageUrl)
  const outcome = getFlowOutcomeDescription(visitor)

  const descriptionParts: string[] = []

  // Add page URL
  descriptionParts.push(`<div class="mb-2">
    <div class="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Page</div>
    <div class="text-sm font-medium text-gray-900 dark:text-gray-100" title="${pageUrl}">${truncatedUrl}</div>
  </div>`)

  // Add outcome if available
  if (outcome) {
    descriptionParts.push(`<div>
      <div class="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Outcome</div>
      <div class="text-sm font-medium text-gray-900 dark:text-gray-100">${outcome}</div>
    </div>`)
  }

  showTooltip(event, {
    headline: 'Flow Execution',
    description: `<div class="space-y-1">${descriptionParts.join('')}</div>`
  })
}

// Process node analytics
const nodeAnalytics = computed(() => {
  if (!flowMetrics.value?.nodeMetrics) return []

  // Convert object to array and sort by visitor count
  return Object.values(flowMetrics.value.nodeMetrics)
    .filter((node) => node.nodeType !== 'root' && node.visitorCount > 0)
    .sort((a, b) => b.visitorCount - a.visitorCount)
})

// Get drop-off nodes
const dropOffNodes = computed(() => {
  return flowMetrics.value?.pathFlows?.dropOffs || []
})

// Helper to find actual node in flow definition
const findNodeInFlow = (nodeId) => {
  if (!nodeId || !offerFunnel.value?.funnelDefinition) return null

  // Check if this is a special node ID (like question_answer nodes)
  // and try to find the actual question node
  const actualNodeId = nodeId.replace('_answer', '').replace('_conversion', '')

  // Use the utility function that checks both id and _uuid
  let foundNode = getNodeByNodeId(offerFunnel.value.funnelDefinition, nodeId)

  // If not found, try with the cleaned node ID
  if (!foundNode && actualNodeId !== nodeId) {
    foundNode = getNodeByNodeId(
      offerFunnel.value.funnelDefinition,
      actualNodeId
    )
  }

  // If still not found, try to extract entity ID from composite format
  // and search by offerId or segmentGroupId
  if (!foundNode) {
    // Try to extract offerId (format: offer_ofr_xxx or offer_conversion_ofr_xxx)
    const offerIdMatch = nodeId.match(/(?:offer|offer_conversion)_(ofr_[a-z0-9]+)/)
    if (offerIdMatch) {
      foundNode = findNodeByOfferId(
        offerFunnel.value.funnelDefinition,
        offerIdMatch[1]
      )
    }

    // Try to extract segmentGroupId (format: sg_dim_xxx or question_answer_dim_xxx)
    if (!foundNode) {
      const sgIdMatch = nodeId.match(/(?:sg|question_answer)_(dim_[a-z0-9]+)/)
      if (sgIdMatch) {
        foundNode = findNodeBySegmentGroupId(
          offerFunnel.value.funnelDefinition,
          sgIdMatch[1]
        )
      }
    }
  }

  return foundNode
}

// Helper to find node by offerId in flow definition
const findNodeByOfferId = (node, offerId) => {
  if (!node) return null
  if (node.offerId === offerId) return node

  // Check child arrays
  const childArrays = ['child', 'yes', 'no']
  for (const key of childArrays) {
    if (Array.isArray(node[key])) {
      for (const child of node[key]) {
        const found = findNodeByOfferId(child, offerId)
        if (found) return found
      }
    }
  }

  // Check split branches
  if (Array.isArray(node.split)) {
    for (const branch of node.split) {
      if (Array.isArray(branch.child)) {
        for (const child of branch.child) {
          const found = findNodeByOfferId(child, offerId)
          if (found) return found
        }
      }
    }
  }

  return null
}

// Helper to find node by segmentGroupId in flow definition
const findNodeBySegmentGroupId = (node, segmentGroupId) => {
  if (!node) return null
  if (node.segmentGroupId === segmentGroupId) return node

  // Check child arrays
  const childArrays = ['child', 'yes', 'no']
  for (const key of childArrays) {
    if (Array.isArray(node[key])) {
      for (const child of node[key]) {
        const found = findNodeBySegmentGroupId(child, segmentGroupId)
        if (found) return found
      }
    }
  }

  // Check split branches
  if (Array.isArray(node.split)) {
    for (const branch of node.split) {
      if (Array.isArray(branch.child)) {
        for (const child of branch.child) {
          const found = findNodeBySegmentGroupId(child, segmentGroupId)
          if (found) return found
        }
      }
    }
  }

  return null
}

// Calculate key insights
const keyInsights = computed(() => {
  if (!flowMetrics.value?.nodeMetrics || !dropOffNodes.value) return null

  const nodeMetrics = flowMetrics.value.nodeMetrics
  const dropOffs = dropOffNodes.value

  // 1. Find question with lowest answer rate
  let highestDropOffQuestion = null
  const questionNodes = Object.values(nodeMetrics).filter(
    (node) =>
      node.nodeType === 'sg' && node.answerDistribution && node.visitorCount > 0
  )

  questionNodes.forEach((question) => {
    // Calculate answer rate based on answer distribution
    const totalAnswers = Object.values(question.answerDistribution).reduce(
      (sum, count) => sum + count,
      0
    )
    const answerRate =
      question.visitorCount > 0
        ? (totalAnswers / question.visitorCount) * 100
        : 100
    const dropOffRate = 100 - answerRate

    if (
      !highestDropOffQuestion ||
      dropOffRate > highestDropOffQuestion.dropOffRate
    ) {
      highestDropOffQuestion = {
        nodeId: question.nodeId,
        node: findNodeInFlow(question.nodeId),
        label: getNodeLabel(question),
        dropOffRate,
        dropOffCount: question.visitorCount - totalAnswers,
        visitorCount: question.visitorCount
      }
    }
  })

  // 2. Find offer/form with highest abandonment rate (lowest conversion rate)
  let highestAbandonmentOffer = null
  const offerNodes = Object.values(nodeMetrics).filter(
    (node) => node.nodeType === 'offer' && node.visitorCount > 0
  )

  offerNodes.forEach((offer) => {
    const abandonmentRate = 100 - (offer.conversionRate || 0)

    if (
      !highestAbandonmentOffer ||
      abandonmentRate > highestAbandonmentOffer.abandonmentRate
    ) {
      highestAbandonmentOffer = {
        nodeId: offer.nodeId,
        node: findNodeInFlow(offer.nodeId),
        label: getNodeLabel(offer),
        abandonmentRate,
        conversionRate: offer.conversionRate || 0,
        conversions: offer.conversionCount || 0,
        visitorCount: offer.visitorCount
      }
    }
  })

  // 3. Find question that takes longest to answer
  let slowestQuestion = null

  // Look for question_answer nodes which track answer duration
  const answerNodes = Object.values(nodeMetrics).filter(
    (node) => node.nodeType === 'question_answer' && node.avgDurationMs > 0
  )

  answerNodes.forEach((answerNode) => {
    // Extract the question node ID from the answer node ID (remove '_answer' suffix)
    const questionNodeId = answerNode.nodeId.replace('_answer', '')
    const questionNode = nodeMetrics[questionNodeId]

    if (
      questionNode &&
      (!slowestQuestion ||
        answerNode.avgDurationMs > slowestQuestion.avgDuration)
    ) {
      slowestQuestion = {
        nodeId: questionNodeId,
        node: findNodeInFlow(questionNodeId),
        label: getNodeLabel(questionNode),
        avgDuration: answerNode.avgDurationMs
      }
    }
  })

  // If no answer nodes found, fall back to question nodes with duration
  if (!slowestQuestion) {
    questionNodes.forEach((question) => {
      if (
        question.avgDurationMs > 0 &&
        (!slowestQuestion ||
          question.avgDurationMs > slowestQuestion.avgDuration)
      ) {
        slowestQuestion = {
          nodeId: question.nodeId,
          node: findNodeInFlow(question.nodeId),
          label: getNodeLabel(question),
          avgDuration: question.avgDurationMs
        }
      }
    })
  }

  return {
    highestDropOffQuestion,
    highestAbandonmentOffer,
    slowestQuestion
  }
})

const isLocal = import.meta.env.VITE_APP_ENV === 'development'

const copyFlowForLLMs = () => {
  const teamPid = teamStore.currentTeam?.pid
  const flowId = offerFunnel.value.id
  const backendUrl = import.meta.env.VITE_APP_BACKEND_HOST || ''

  // Open the YAML export in a new tab
  const url = `${backendUrl}/${teamPid}/downloads/flow/${flowId}/yaml`
  window.open(url, '_blank')
}

const copyFlowJson = async () => {
  try {
    const flowJson = JSON.stringify(offerFunnel.value, null, 2)
    await navigator.clipboard.writeText(flowJson)
    notificationStore.addNotification('Flow JSON copied to clipboard')
  } catch (error) {
    console.error('Failed to copy flow JSON:', error)
    notificationStore.addError('Failed to copy flow JSON to clipboard')
  }
}

const promptDuplicate = () => {
  confirm.require({
    message: `Are you sure you want to duplicate this Flow?`,
    header: 'Really Duplicate?',
    acceptProps: { label: 'Duplicate', severity: 'info' },
    rejectProps: { label: 'Cancel', outlined: true },
    accept: async () => {
      const flow = offerFunnel.value
      const newFlow = flowStore.duplicate(flow)
      router.push({
        name: ROUTES.OFFERFUNNEL,
        params: { offerFunnelId: newFlow.id }
      })
    }
  })
}

const confirmDeleteFlow = () => {
  confirm.require({
    message: [
      'Are you sure you want to delete this Flow?',
      associatedWidgetsDeleteMessage.value
    ]
      .filter(Boolean)
      .join(' '),
    header: 'Really Delete?',
    acceptProps: { label: 'Delete', severity: 'danger' },
    rejectProps: { label: 'Cancel', outlined: true },
    accept: async () => {
      await deleteFlow({
        deleteWidgets: false,
        deleteReferencedThings: false
      })
    }
  })
}

const deleteFlow = async ({
  deleteWidgets,
  deleteReferencedThings
}: DeleteFlowOptions) => {
  const flow = offerFunnel.value
  if (!flow) return

  const widgetsToDelete = deleteWidgets ? [...associatedWidgets.value] : []
  const referencedResources = deleteReferencedThings
    ? [...referencedFlowResources.value]
    : []

  isDeletingFlow.value = true
  showDeleteFlowModal.value = false

  try {
    await router.push({ name: ROUTES.OFFERFUNNELS })

    if (deleteWidgets) {
      destroyAssociatedWidgets(widgetsToDelete)
    }

    if (deleteReferencedThings) {
      destroyReferencedFlowResources(referencedResources)
    }

    flowStore.destroy(flow)
  } finally {
    isDeletingFlow.value = false
  }
}

const deleteFlowOnly = () =>
  deleteFlow({
    deleteWidgets: false,
    deleteReferencedThings: false
  })

const deleteFlowAndWidgets = () =>
  deleteFlow({
    deleteWidgets: true,
    deleteReferencedThings: false
  })

const deleteFlowAndWidgetsAndReferencedThings = () =>
  deleteFlow({
    deleteWidgets: true,
    deleteReferencedThings: true
  })

const promptDelete = () => {
  if (referencedFlowResources.value.length === 0) {
    confirmDeleteFlow()
    return
  }

  showDeleteFlowModal.value = true
}

const promptClearAnalytics = () => {
  confirm.require({
    message: `Are you sure you want to clear all analytics data for this Flow? This action cannot be undone and will permanently delete all visitor tracking data, flow executions, and metrics for "${offerFunnel.value?.name}".`,
    header: 'Clear Flow Analytics Data?',
    acceptProps: { label: 'Clear Data', severity: 'danger' },
    rejectProps: { label: 'Cancel', outlined: true },
    accept: async () => {
      try {
        const result = await deleteFlowExecutions(
          teamStore.currentTeam.pid,
          offerFunnel.value.id
        )

        if (result.success) {
          notificationStore.addNotification(
            `Successfully deleted ${result.deleted} flow execution records.`
          )

          // Refresh flow metrics
          const flowQuery = flowMetricsStore.flowQueries[offerFunnel.value.id]
          if (flowQuery) {
            await flowQuery.refetch()
          }

          // Force refresh of heat map display if active
          if (flowBuilderStore.showHeatMap || flowBuilderStore.showPathFlow) {
            // Toggle off and on to force re-render with new data
            const wasShowingHeatMap = flowBuilderStore.showHeatMap
            const wasShowingPathFlow = flowBuilderStore.showPathFlow

            flowBuilderStore.showHeatMap = false
            flowBuilderStore.showPathFlow = false

            await nextTick()

            flowBuilderStore.showHeatMap = wasShowingHeatMap
            flowBuilderStore.showPathFlow = wasShowingPathFlow
          }
        } else {
          throw new Error('Failed to clear analytics data')
        }
      } catch (error) {
        notificationStore.addError(
          'Failed to clear flow analytics data. Please try again.'
        )
        console.error('Error clearing flow analytics:', error)
      }
    }
  })
}

const isDragging = computed(() => flowBuilderStore.isDragging)

// Get adapter from injected props
const adapter = inject('adapter', null)

const handleDragStart = (event, nodeData) => {
  // Hide tooltip when drag starts
  clearTooltip()

  // Wrap the node data in the expected format
  const dragData = { node: nodeData.node || nodeData, isExisting: false }

  // Use adapter if available, otherwise fall back to store
  if (adapter?.setDragging) {
    adapter.setDragging(dragData)
  } else {
    flowBuilderStore.isDragging = dragData
  }
  event.dataTransfer.setData('node', JSON.stringify(nodeData))
}

const handleDragEnd = (event, node) => {
  // Use adapter if available, otherwise fall back to store
  if (adapter?.setDragging) {
    adapter.setDragging(null)
  } else {
    flowBuilderStore.isDragging = null
  }
}

const associatedWidgets = computed(() =>
  widgetStore.getWidgetsForOfferFunnel(route.params.offerFunnelId)
)

const associatedWidgetsSummary = computed(() => {
  if (associatedWidgets.value.length === 0) return ''

  return `Attached widgets: ${associatedWidgets.value
    .map((widget) => widget.name)
    .join(', ')}`
})

const associatedWidgetsDeleteMessage = computed(() => {
  if (associatedWidgets.value.length === 0) return ''

  return `Any attached widgets will be left in place but detached from this flow: ${associatedWidgets.value
    .map((widget) => widget.name)
    .join(', ')}`
})

const referencedFlowResources = computed<FlowReference[]>(() => {
  const flow = offerFunnel.value
  if (!flow?.funnelDefinition) return []

  const resources: FlowReference[] = []
  const seen = new Set<string>()

  for (const question of questionsInFlow(flow.funnelDefinition)) {
    const key = `question:${question.id}`
    if (seen.has(key)) continue

    seen.add(key)
    resources.push({
      key,
      id: question.id,
      kind: 'question',
      label: question.label,
      dimensionId: question.dimensionId,
      sharedBy: flowStore.flows
        .filter(
          (candidate) =>
            candidate.id !== flow.id &&
            flowContainsQuestion(candidate, question)
        )
        .map((candidate) => candidate.name)
    })
  }

  for (const offer of offersInFlow(flow.funnelDefinition)) {
    const key = `offer:${offer.id}`
    if (seen.has(key)) continue

    seen.add(key)
    resources.push({
      key,
      id: offer.id,
      kind: isForm(offer) ? 'form' : 'offer',
      label: offer.name,
      dimensionId: null,
      sharedBy: offerUsedBy(offer)
        .filter((candidate) => candidate.id !== flow.id)
        .map((candidate) => candidate.name)
    })
  }

  // segmentGroupStore.destroy() cascade-deletes campaigns whose dimension_id
  // matches a deleted question's dimension — surface them so the user is warned.
  const questionDimensionIds = new Set(
    resources
      .filter((resource) => resource.kind === 'question' && resource.dimensionId)
      .map((resource) => resource.dimensionId)
  )

  for (const campaign of campaignStore.campaigns) {
    if (!campaign.dimension_id || !questionDimensionIds.has(campaign.dimension_id)) continue

    const key = `campaign:${campaign.id}`
    if (seen.has(key)) continue

    seen.add(key)
    resources.push({
      key,
      id: campaign.id,
      kind: 'campaign',
      label: campaign.name,
      dimensionId: campaign.dimension_id,
      sharedBy: []
    })
  }

  return resources.sort((left, right) => left.label.localeCompare(right.label))
})

const flowReferenceKindLabel = (reference: FlowReference) => {
  switch (reference.kind) {
    case 'form':
      return 'Form'
    case 'offer':
      return 'Offer'
    case 'campaign':
      return 'Campaign'
    default:
      return 'Question'
  }
}

const flowReferenceUsage = (reference: FlowReference) => {
  if (reference.kind === 'campaign') {
    return "Targets segments from this flow's questions"
  }

  if (reference.sharedBy.length === 0) {
    return 'Used only by this flow'
  }

  if (reference.sharedBy.length === 1) {
    return `Also used by ${reference.sharedBy[0]}`
  }

  const otherFlowCount = reference.sharedBy.length - 1
  return `Also used by ${reference.sharedBy[0]} and ${otherFlowCount} other ${otherFlowCount === 1 ? 'flow' : 'flows'}`
}

const destroyReferencedFlowResources = (references: FlowReference[]) => {
  references.forEach((reference) => {
    if (reference.kind === 'campaign') {
      // Deleted by segmentGroupStore.destroy() when its dimension is removed.
      return
    }

    if (reference.kind === 'question') {
      if (reference.dimensionId) {
        const segmentGroup = segmentGroupStore.getById(reference.dimensionId)
        if (segmentGroup) {
          segmentGroupStore.destroy(segmentGroup)
          return
        }
      }

      const question = questionStore.getById(reference.id)
      if (question) {
        questionStore.destroy(question)
      }

      return
    }

    const offer = offerStore.getById(reference.id)
    if (offer) {
      offerStore.destroy(offer)
    }
  })
}

const destroyAssociatedWidgets = (widgets: Widget[]) => {
  widgets.forEach((widget) => {
    widgetStore.destroy(widget)
  })
}

const widgetDefinition = (widget) => {
  return widgetTypeDefinitions.find((w) => w.id === widget.type)
}

const widgetImagePath = (widget) => {
  return `/img/widget/type/${widget.type}.svg`
}

// Helper methods for stats
const formatDuration = (ms) => {
  if (!ms || ms === 0) return '0s'

  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`
  } else {
    return `${seconds}s`
  }
}

const calculateCompletionRate = (breakdown) => {
  if (!breakdown) return 0
  const total = Object.values(breakdown).reduce((sum, count) => sum + count, 0)
  const completed = breakdown.completed || 0
  return total > 0 ? ((completed / total) * 100).toFixed(1) : 0
}

// Node type labels mapping
const nodeTypeLabels = {
  offer: 'Offer',
  sg: 'Segment',
  yesno: 'Condition',
  redirect: 'Redirect',
  message: 'Message',
  data: 'Set Data',
  code: 'Code',
  random: 'Split',
  highscore: 'High Score',
  exit: 'Exit'
}

const getNodeLabel = (node) => {
  if (!node) return 'Unknown Node'

  // First try to find the actual node in the flow definition
  let actualNode = findNodeInFlow(node.nodeId)

  if (actualNode) {
    // Get a descriptive label based on node type
    const typeLabel = nodeTypeLabels[actualNode.type] || actualNode.type

    switch (actualNode.type) {
      case 'offer':
        const offer = offerStore.getById(actualNode.offerId)
        return offer ? offer.name : typeLabel
      case 'sg':
        const segmentGroup = segmentGroupStore.getById(
          actualNode.segmentGroupId
        )
        if (actualNode.ask && segmentGroup) {
          return segmentGroup.name
        } else if (segmentGroup) {
          return `Router: ${segmentGroup.name}`
        }
        return actualNode.ask ? 'Question' : 'Router'
      case 'yesno':
        return actualNode.notes || 'Condition'
      case 'redirect':
        return `Redirect: ${actualNode.url || 'URL'}`
      case 'message':
        return `Message: ${actualNode.content?.substring(0, 30)}...`
      default:
        return typeLabel
    }
  }

  // If we couldn't find the actual node in the flow definition, try to extract
  // entity IDs from the composite nodeId format (e.g., "offer_ofr_xxx" or "sg_dim_xxx")
  // and look up the entity directly in stores
  if (node.nodeId) {
    // Extract offerId from composite nodeId (format: offer_ofr_xxx or offer_conversion_ofr_xxx)
    const offerIdMatch = node.nodeId.match(/(?:offer|offer_conversion)_(ofr_[a-z0-9]+)/)
    if (offerIdMatch) {
      const offer = offerStore.getById(offerIdMatch[1])
      if (offer?.name) {
        return offer.name
      }
    }

    // Extract segmentGroupId from composite nodeId (format: sg_dim_xxx or question_answer_dim_xxx)
    const sgIdMatch = node.nodeId.match(/(?:sg|question_answer)_(dim_[a-z0-9]+)/)
    if (sgIdMatch) {
      const segmentGroup = segmentGroupStore.getById(sgIdMatch[1])
      if (segmentGroup?.name) {
        // Distinguish between questions and routers based on answerDistribution
        if (node.answerDistribution || node.nodeType === 'question_answer') {
          return segmentGroup.name
        }
        return `Router: ${segmentGroup.name}`
      }
    }
  }

  // If we still couldn't find a name, use generic labels
  if (node.nodeType === 'sg' || node.nodeType === 'question_answer') {
    return node.answerDistribution ? 'Question' : 'Router'
  } else if (node.nodeType === 'offer' || node.nodeType === 'offer_conversion') {
    return 'Offer'
  }

  // Fallback to node type
  return nodeTypeLabels[node.nodeType] || node.nodeType
}

const getNodeById = (nodeId) => {
  return nodeAnalytics.value.find((n) => n.nodeId === nodeId)
}

const getSegmentName = (segmentId) => {
  if (segmentId === 'fallback') return 'Other'

  // Find the segment across all segment groups
  const segment = segmentGroupStore.segmentGroups
    .flatMap((sg) => sg.segments)
    .find((s) => s.id === segmentId)

  if (segment) {
    return segmentName(segment) || segment.name || segmentId
  }

  return segmentId
}

const getConversionRateClass = (rate) => {
  if (!rate || rate === 0) return 'text-gray-500'
  if (rate >= 10) return 'text-green-600 dark:text-green-400 font-medium'
  if (rate >= 5) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

// Handle focus node for insights
const handleFocusNode = (insight) => {
  if (insight.node) {
    emits('focusNode', insight.node)
  } else {
    // Log for debugging
    console.warn('Node not found in flow:', insight.nodeId)
    notificationStore.addError('This node may have been removed from the flow.')
  }
}

// Function to focus on a node with an error
const focusOnNode = (node) => {
  // Emit an event that FlowShow can listen to
  emits('focusNode', node)
}

// Tooltip event handlers
const handleShowTooltip = (event, node) => {
  showTooltip(event, node.tooltip)
}

// Expose toggle method for keyboard shortcut
defineExpose({
  toggle: () => {
    isCollapsed.value = !isCollapsed.value
  }
})
</script>
